import { useEffect } from "react"
import { Outlet } from "react-router-dom"
import "../../pages/shareViewer/assets/shareViewer.css"
import { startScrollNudge } from "../../pages/shareViewer/utils/scrollNudge"
import { isMobileDevice } from "../../pages/shareViewer/utils/isMobileDevice"

// เปลือกของหน้าเว็บ viewer สาธารณะ (/t/:token) — เต็มจอ ไม่มี sidebar/header ของ admin เลย
// (คนละกลุ่มกับ ServicesLayout ที่มี logo+เปลี่ยนภาษาไว้ให้ engineer ติดตั้งอุปกรณ์ — หน้านี้ไม่ต้องมี)
//
// Shell for the public web viewer (/t/:token) — full screen, no admin sidebar/header at all (unlike
// ServicesLayout which carries a logo + language switch for install engineers — not needed here).
export default function ShareViewerLayout() {
  // ⚠️ ความสูงของ shell มาจาก CSS (`100lvh` ใน shareViewer.css) เท่านั้น — เคยมี inline px จาก
  // `useViewportHeight()` ทับอยู่ที่นี่ (2026-09-07) แล้วถอดออก เพราะ `window.innerHeight` คือ small
  // viewport = ต้นเหตุของช่องว่างที่พยายามแก้อยู่ ไม่ใช่ทางแก้ (ดูคอมเมนต์เต็มใน shareViewer.css)
  // hook นั้นยังใช้อยู่ที่ VehicleSheet/StopSheet ซึ่ง "ต้องการ" small viewport จริงๆ (แผ่นข้อมูลต้องอยู่
  // ในพื้นที่ที่มองเห็นได้ ไม่ใช่ใต้ browser chrome)
  //
  // ⚠️ The shell's height comes from CSS (`100lvh` in shareViewer.css) only — an inline px value from
  // `useViewportHeight()` used to override it here (2026-09-07) and was removed, because
  // `window.innerHeight` is the small viewport, i.e. the cause of the very gap being fixed, not the fix
  // (see the full comment in shareViewer.css). That hook is still used by VehicleSheet/StopSheet, which
  // genuinely do want the small viewport (a sheet must sit inside the visible area, not under the
  // browser chrome).

  // ticket 18: token อยู่ใน URL ของหน้านี้ — ตั้ง Referrer-Policy เฉพาะหน้านี้ (meta tag, ไม่แก้ header
  // ของทั้งแอปที่ web server เป็นคนคุม) กัน token หลุดผ่าน Referer header เมื่อโหลด resource ข้ามโดเมน
  // (ฟอนต์/Maps tile) แล้วลบ tag ทิ้งตอนออกจากหน้าไม่ให้กระทบหน้าอื่น
  //
  // ticket 18: this page's token lives in the URL — scope Referrer-Policy to this page only (a meta
  // tag, not the whole app's server-controlled headers) so it can't leak via the Referer header when
  // loading cross-origin resources (fonts/map tiles), then remove the tag on unmount
  useEffect(() => {
    const meta = document.createElement("meta")
    meta.name = "referrer"
    meta.content = "no-referrer"
    document.head.appendChild(meta)
    return () => document.head.removeChild(meta)
  }, [])

  // เพิ่ม viewport-fit=cover เฉพาะหน้านี้ โดยแก้ content ของ meta viewport ตัวเดิม (ไม่ได้แก้ไฟล์
  // public/index.html ที่ทั้งแอป admin ใช้ร่วมกัน — แก้แค่ DOM ตอน mount แล้วคืนค่าเดิมตอน unmount, ห้าม
  // append meta viewport ซ้ำเป็นตัวที่สอง เพราะเบราว์เซอร์รวม/เลือกใช้หลาย viewport tag ไม่ตรงกันทุกตัว)
  // เปิดใช้งาน env(safe-area-inset-*) ให้แผ่นข้อมูล/ปุ่มควบคุมแผนที่หลบขอบจอ/home indicator ของ iPhone
  // ได้ — ไม่งั้น env() จะได้ 0 เสมอ
  //
  // Add viewport-fit=cover for this page only, by editing the existing viewport meta tag's content
  // (not public/index.html, shared by the whole admin app — DOM-only on mount, restored on unmount;
  // don't append a second viewport meta tag, since browsers don't merge/pick between multiple ones
  // consistently). This activates env(safe-area-inset-*) so the info sheet / map controls can dodge
  // the notch/home-indicator area on iPhone; without it env() always resolves to 0
  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]')
    if (!meta) return undefined
    const original = meta.getAttribute("content")
    meta.setAttribute("content", `${original}, viewport-fit=cover`)
    return () => meta.setAttribute("content", original)
  }, [])

  // nudge scroll 1px เพื่อให้ browser chrome (address bar/toolbar) ของมือถือหุบเอง แทนรอ user ลากมือ —
  // ดูเหตุผลเต็มใน scrollNudge.js (มี test คู่กันที่ src/__tests__/scrollNudge.test.js)
  // ⚠️ เฉพาะอุปกรณ์มือถือจริง — desktop ไม่มี chrome ที่หุบได้อยู่แล้ว การทำ document สูงเกิน viewport
  // 1px จึงได้แค่ scrollbar แนวตั้งโผล่มาเปล่าๆ (code review 2026-09-07) · ใช้ `isMobileDevice()` (เช็ค
  // อุปกรณ์จริง) ไม่ใช่ `mobileOnly` ของ useResponsive (เช็คความกว้าง viewport) เพราะเกณฑ์คือ "มี browser
  // chrome ที่หุบตาม scroll ไหม" ไม่ใช่ "จอกว้างเท่าไหร่" — เกณฑ์เดียวกับ HIDE_MAP_CONTROLS ใน ViewerMap.js
  //
  // nudge a 1px scroll so a mobile browser's chrome (address bar/toolbar) collapses on its own instead
  // of waiting for the user's finger — see scrollNudge.js for the full reasoning (tests alongside it in
  // src/__tests__/scrollNudge.test.js)
  // ⚠️ Real mobile devices only — desktop has no collapsible chrome, so making the document 1px taller
  // than the viewport there only produces a stray vertical scrollbar (code review 2026-09-07). Uses
  // `isMobileDevice()` (checks the actual device), not useResponsive's `mobileOnly` (checks viewport
  // width), since the deciding factor is "is there browser chrome that collapses on scroll", not "how
  // wide is the screen" — the same criterion HIDE_MAP_CONTROLS uses in ViewerMap.js
  useEffect(() => (isMobileDevice() ? startScrollNudge() : undefined), [])

  return (
    <div className="share-viewer-shell">
      <Outlet />
    </div>
  )
}
